import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  FaSms, FaEnvelope, FaRobot, FaUser, FaChevronLeft, FaChevronRight,
  FaChevronDown, FaChevronUp, FaSpinner, FaSyncAlt, FaTimes, FaExclamationTriangle,
} from 'react-icons/fa';
import { getMessageHistory } from '../../services/messagingService';

// History of messages the system has sent (ticket 14). Two kinds of row:
//   - kind:'broadcast'  → a manual send from the Messaging page (grouped, expandable to recipients)
//   - kind:'message'    → a single automated system send (invoice, RCTI, quote, welcome, overdue, …)
// The server returns the whole (capped) date range; Method/Type filtering and pagination happen here in
// memory over the full set, so a filter never hides matching rows on an unseen page.

const PAGE_SIZE = 25;
const FETCH_LIMIT = 500; // matches the server-side MAX_HISTORY cap
const DEFAULT_RANGE_DAYS = 7; // default view: the past week

const pad = (n) => String(n).padStart(2, '0');
const toDateInputValue = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// Local start/end of a yyyy-mm-dd, as ISO — both bounds use the local calendar day so the range is symmetric.
const startOfLocalDayISO = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
};
const endOfLocalDayISO = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
};
const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - DEFAULT_RANGE_DAYS);
  return toDateInputValue(d);
};
const defaultTo = () => toDateInputValue(new Date());

const METHOD_META = {
  sms: { label: 'SMS', cls: 'bg-blue-100 text-blue-700', Icon: FaSms },
  email: { label: 'Email', cls: 'bg-purple-100 text-purple-700', Icon: FaEnvelope },
};
const TYPE_META = {
  manual: { label: 'Manual', cls: 'bg-gray-100 text-gray-700', Icon: FaUser },
  automated: { label: 'Automated', cls: 'bg-amber-100 text-amber-800', Icon: FaRobot },
};
const OUTCOME_META = {
  sent: { label: 'Sent', cls: 'bg-green-100 text-green-800' },
  partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-800' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-800' },
  pending: { label: 'Pending', cls: 'bg-blue-100 text-blue-700' },
};
// Per-recipient status pill colours (shared palette with MessagingPage's delivery-details panel).
const STATUS_BADGE = {
  sent: 'bg-green-100 text-green-800',
  skipped: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-800',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// "38 sent · 2 failed · 1 skipped", dropping any zero parts (broadcast rows only).
const countsSummary = (e) => {
  const parts = [];
  if (e.sent) parts.push(`${e.sent} sent`);
  if (e.failed) parts.push(`${e.failed} failed`);
  if (e.skipped) parts.push(`${e.skipped} skipped`);
  return parts.join(' · ');
};

const Pill = ({ meta }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${meta.cls}`}>
    {meta.Icon && <meta.Icon className="h-3 w-3" />} {meta.label}
  </span>
);

// ─── Row (expandable) ───────────────────────────────────────────────────────────
const HistoryRow = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);

  const isBroadcast = entry.kind === 'broadcast';
  const method = METHOD_META[entry.channel] || { label: entry.channel || '—', cls: 'bg-gray-100 text-gray-600' };
  const type = entry.automated ? TYPE_META.automated : TYPE_META.manual;
  const outcome = OUTCOME_META[entry.outcome] || { label: entry.outcome || '—', cls: 'bg-gray-100 text-gray-600' };
  const preview = entry.channel === 'email' ? (entry.subject || entry.body) : entry.body;
  const recipients = Array.isArray(entry.recipients) ? entry.recipients : [];

  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpanded((v) => !v)}>
        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDateTime(entry.timestamp)}</td>
        <td className="px-4 py-3"><Pill meta={method} /></td>
        <td className="px-4 py-3"><Pill meta={type} /></td>
        <td className="px-4 py-3 text-xs text-gray-600 max-w-[260px] truncate">
          {preview || '—'}
          {entry.testMode && <span className="ml-1 text-[10px] uppercase text-amber-600 font-semibold">test</span>}
        </td>
        <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap max-w-[180px] truncate">
          {isBroadcast ? (
            <>
              {entry.broadcast && <span className="mr-1 inline-block bg-jims-cyan/10 text-jims-cyan-dark rounded px-1.5 py-0.5 font-medium">Broadcast</span>}
              {entry.attempted ?? recipients.length}
            </>
          ) : (entry.to || '—')}
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <Pill meta={outcome} />
          {isBroadcast && countsSummary(entry) && (
            <span className="ml-2 text-xs text-gray-400">{countsSummary(entry)}</span>
          )}
        </td>
        <td className="px-4 py-3 text-center text-gray-400">
          {expanded ? <FaChevronUp className="inline h-3 w-3" /> : <FaChevronDown className="inline h-3 w-3" />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan={7} className="px-6 py-4">
            <div className="grid gap-4 md:grid-cols-3">
              {/* Message content */}
              <div className="md:col-span-1 space-y-2">
                {entry.channel === 'email' && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Subject</p>
                    <p className="text-sm text-gray-800">{entry.subject || '—'}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Message</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                    {entry.body || (entry.channel === 'email' ? '(email body not stored)' : '—')}
                  </p>
                </div>
                {entry.sentBy && <p className="text-xs text-gray-400">Sent by {entry.sentBy}</p>}
                {!isBroadcast && entry.to && <p className="text-xs text-gray-400">To {entry.to}</p>}
              </div>

              {/* Per-recipient breakdown (broadcasts only) */}
              {isBroadcast && (
                <div className="md:col-span-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Recipients ({recipients.length}{entry.recipientsTruncated ? '+' : ''})
                  </p>
                  {recipients.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No recipient detail recorded.</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                      <div className="max-h-64 overflow-y-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              <th className="px-3 py-2">Franchisee</th>
                              <th className="px-3 py-2">To</th>
                              <th className="px-3 py-2">Status</th>
                              <th className="px-3 py-2">Detail</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recipients.map((r, i) => (
                              <tr key={`${r.franchiseeId || r.name || 'r'}-${i}`} className="border-b border-gray-100">
                                <td className="px-3 py-2 text-gray-900">{r.name || '—'}</td>
                                <td className="px-3 py-2 text-gray-600 break-all">{r.to || '—'}</td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[r.status] || 'bg-gray-100 text-gray-600'}`}>
                                    {r.status}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-500">{r.reason || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {entry.recipientsTruncated && (
                        <p className="px-3 py-2 text-xs text-gray-400 italic border-t border-gray-100">
                          Recipient list truncated — showing the first {recipients.length}.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ─── History view ───────────────────────────────────────────────────────────────
const MessageHistory = () => {
  const [entries, setEntries] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Server-side filter (date range)
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  // Client-side filters
  const [method, setMethod] = useState('all'); // 'all' | 'sms' | 'email'
  const [type, setType] = useState('all'); // 'all' | 'manual' | 'automated'
  const [pageIndex, setPageIndex] = useState(0);

  // Guards against a stale (slow) response overwriting a newer one after the date filter changes.
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const filters = { limit: FETCH_LIMIT };
      if (from) filters.from = startOfLocalDayISO(from);
      if (to) filters.to = endOfLocalDayISO(to);
      const res = await getMessageHistory(filters);
      if (reqId !== reqIdRef.current) return; // a newer request superseded this one
      setEntries(res.entries || []);
      setTruncated(!!res.truncated);
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      setError(err.message || 'Failed to load message history.');
      setEntries([]);
      setTruncated(false);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  // In-memory Method/Type filtering over the FULL fetched range (not just one page).
  const displayed = useMemo(
    () =>
      entries.filter(
        (e) =>
          (method === 'all' || e.channel === method) &&
          (type === 'all' || (type === 'automated' ? !!e.automated : !e.automated))
      ),
    [entries, method, type]
  );

  // Reset to page 1 whenever the filtered set changes.
  useEffect(() => {
    setPageIndex(0);
  }, [method, type, entries]);

  const pageCount = Math.max(1, Math.ceil(displayed.length / PAGE_SIZE));
  const safePage = Math.min(pageIndex, pageCount - 1);
  const pageRows = displayed.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const clearFilters = () => {
    setMethod('all');
    setType('all');
    setFrom(defaultFrom());
    setTo(defaultTo());
  };
  const hasFilters = method !== 'all' || type !== 'all' || from !== defaultFrom() || to !== defaultTo();

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow px-5 py-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Method</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-jims-cyan"
            >
              <option value="all">All</option>
              <option value="sms">SMS</option>
              <option value="email">Email</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-jims-cyan"
            >
              <option value="all">All</option>
              <option value="manual">Manual (broadcast)</option>
              <option value="automated">Automated</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-jims-cyan"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-jims-cyan"
            />
          </div>

          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan disabled:opacity-50"
          >
            <FaSyncAlt className={loading ? 'animate-spin' : ''} /> Refresh
          </button>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded ml-auto"
            >
              <FaTimes className="h-3 w-3" /> Clear Filters
            </button>
          )}
        </div>

        {truncated && (
          <p className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            <FaExclamationTriangle className="h-3 w-3 shrink-0" />
            Showing the most recent {FETCH_LIMIT} messages in this range — narrow the date range to see older ones.
          </p>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {error && (
          <div className="px-5 py-4 text-sm text-red-600 bg-red-50 border-b border-red-100">
            <p className="font-semibold mb-1">Error loading history:</p>
            <pre className="text-xs whitespace-pre-wrap break-all font-mono bg-red-100 rounded p-2">{error}</pre>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <FaSpinner className="animate-spin h-6 w-6 mr-2" /> Loading…
          </div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            {entries.length > 0
              ? 'No messages match the current Method/Type filter.'
              : 'No messages sent in this date range.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Date', 'Method', 'Type', 'Message', 'Recipient(s)', 'Outcome', ''].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.map((entry) => (
                  <HistoryRow key={`${entry.kind}-${entry.id}`} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination footer */}
        {!loading && displayed.length > 0 && (
          <div className="px-5 py-3 bg-gray-50 border-t flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Page {safePage + 1} of {pageCount}&nbsp;·&nbsp;{displayed.length} message{displayed.length === 1 ? '' : 's'}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FaChevronLeft className="h-3 w-3" />
              </button>
              <button
                onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
                disabled={safePage >= pageCount - 1}
                className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FaChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageHistory;
